import { orgLogoUrl, orgLogoHorizontalUrl } from '../../../shell/shared';

/**
 * PRD.239 §9 — Purchase Order Print Layout.
 *
 * Supplier-facing procurement document. Pure presentational component
 * mirroring the shape of InvoicePrintLayout (PRD.238) but with
 * procurement sections: buyer block, supplier block, PO metadata, line
 * items (qty/bonus/rate/amount), totals, terms/notes, signature lines.
 *
 * No DB calls. No stock side-effects. Snapshots from the saved PO/PO
 * lines are the source of truth for historical safety (PRD.239 §12);
 * the caller passes them in via props.
 *
 * Render this inside a hidden `.po-print-only` div and pair it with
 * `PURCHASE_ORDER_PRINT_STYLES` + `triggerPrint('purchase-order')` to
 * actually print — same pattern as InvoicePrintLayout.
 */

// ─── Public types ───

export type PurchaseOrderPrintLogoSource = 'standard' | 'horizontal' | 'none';

export interface PurchaseOrderPrintBuyer {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  taxId?: string;
}

export interface PurchaseOrderPrintSupplier {
  name: string;
  contactPerson?: string;
  address?: string;
  phone?: string;
  email?: string;
  taxId?: string;
  paymentTerms?: string;
}

export interface PurchaseOrderPrintMeta {
  poNumber: string;
  poDateIso: string;
  status?: string;
  amountsAre?: string; // 'tax_exclusive' | 'tax_inclusive'
  preparedBy?: string;
  reference?: string;
}

export interface PurchaseOrderPrintLineItem {
  itemName: string;
  sku?: string;
  unit?: string;
  quantityOrdered: number;
  bonusQuantity: number;
  rate: number;
  amount: number;
  remarks?: string;
}

export interface PurchaseOrderPrintTotals {
  subtotal: number;
  totalAmount: number;
}

export interface PurchaseOrderPrintSettings {
  logoSource: PurchaseOrderPrintLogoSource;
  showSku: boolean;
  showBonus: boolean;
  showRemarks: boolean;
  showSupplierContact: boolean;
  showSignatureBlock: boolean;
  showTerms: boolean;
  footerText: string;
  termsText: string;
  preparedByLabel: string;
  approvedByLabel: string;
  supplierAcknowledgmentLabel: string;
}

export interface PurchaseOrderPrintLayoutProps {
  settings: PurchaseOrderPrintSettings;
  buyer: PurchaseOrderPrintBuyer;
  supplier: PurchaseOrderPrintSupplier;
  meta: PurchaseOrderPrintMeta;
  items: PurchaseOrderPrintLineItem[];
  totals: PurchaseOrderPrintTotals;
  notes?: string;
  /** Currency formatter — caller-supplied so locale stays consistent across surfaces. */
  formatMoney: (amount: number) => string;
  accentColor?: string;
  /** When true, render with screen-friendly chrome (e.g. shadow + scale) for a wizard preview. */
  previewMode?: boolean;
}

const DEFAULT_ACCENT = '#5A2A18';
const A4_WIDTH_MM = '210mm';
const A4_MIN_HEIGHT_MM = '297mm';

// ─── Helpers ───

function resolveLogoUrl(source: PurchaseOrderPrintLogoSource): string {
  switch (source) {
    case 'standard':
      return orgLogoUrl;
    case 'horizontal':
      return orgLogoHorizontalUrl;
    case 'none':
    default:
      return '';
  }
}

function formatPrintDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatAmountsAre(value?: string): string {
  if (value === 'tax_inclusive') return 'Tax inclusive';
  if (value === 'tax_exclusive') return 'Tax exclusive';
  return '—';
}

function statusLabel(status?: string): string {
  if (!status) return '';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

// ─── Main component ───

export function PurchaseOrderPrintLayout({
  settings,
  buyer,
  supplier,
  meta,
  items,
  totals,
  notes,
  formatMoney,
  accentColor,
  previewMode,
}: PurchaseOrderPrintLayoutProps) {
  const accent = accentColor || DEFAULT_ACCENT;
  const logoUrl = resolveLogoUrl(settings.logoSource);
  const showAnyTerms =
    settings.showTerms && (settings.termsText.trim().length > 0 || (notes ?? '').trim().length > 0);

  return (
    <div
      style={{
        width: A4_WIDTH_MM,
        minHeight: A4_MIN_HEIGHT_MM,
        background: '#ffffff',
        color: '#0f172a',
        fontFamily:
          'Inter, "Helvetica Neue", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        fontSize: 11,
        lineHeight: 1.45,
        padding: '14mm 14mm 16mm',
        boxSizing: 'border-box',
        // Wizard preview chrome — disabled when actually printing.
        boxShadow: previewMode ? '0 4px 24px rgba(15,23,42,0.08)' : 'none',
        margin: previewMode ? '0 auto' : 0,
      }}
    >
      {/* Header / branding */}
      <Header logoUrl={logoUrl} buyer={buyer} accent={accent} />

      {/* Title bar */}
      <div
        style={{
          marginTop: 16,
          padding: '10px 14px',
          background: accent,
          color: '#ffffff',
          fontWeight: 700,
          fontSize: 14,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          textAlign: 'center',
        }}
      >
        Purchase Order
      </div>

      {/* PO metadata + Supplier block (two columns) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.1fr 1fr',
          gap: 16,
          marginTop: 16,
        }}
      >
        <SupplierBlock
          supplier={supplier}
          accent={accent}
          showContact={settings.showSupplierContact}
        />
        <MetaBlock meta={meta} accent={accent} />
      </div>

      {/* Line items */}
      <LineItemsTable
        items={items}
        accent={accent}
        showSku={settings.showSku}
        showBonus={settings.showBonus}
        showRemarks={settings.showRemarks}
        formatMoney={formatMoney}
      />

      {/* Totals */}
      <TotalsBlock totals={totals} accent={accent} formatMoney={formatMoney} meta={meta} />

      {/* Terms / Notes */}
      {showAnyTerms && (
        <TermsBlock accent={accent} termsText={settings.termsText} notes={notes ?? ''} />
      )}

      {/* Signature lines */}
      {settings.showSignatureBlock && (
        <SignatureBlock
          accent={accent}
          preparedByLabel={settings.preparedByLabel}
          approvedByLabel={settings.approvedByLabel}
          supplierAckLabel={settings.supplierAcknowledgmentLabel}
          preparedByName={meta.preparedBy ?? ''}
        />
      )}

      {/* Footer */}
      {settings.footerText.trim() && (
        <div
          style={{
            marginTop: 18,
            paddingTop: 10,
            borderTop: `1px solid ${accent}33`,
            textAlign: 'center',
            fontSize: 9.5,
            color: '#475569',
          }}
        >
          {settings.footerText}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───

function Header({
  logoUrl,
  buyer,
  accent,
}: {
  logoUrl: string;
  buyer: PurchaseOrderPrintBuyer;
  accent: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        paddingBottom: 12,
        borderBottom: `2px solid ${accent}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, minWidth: 0 }}>
        {logoUrl && (
          <img
            src={logoUrl}
            alt={buyer.name}
            style={{ width: 64, height: 64, objectFit: 'contain', flexShrink: 0 }}
          />
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: accent, letterSpacing: 0.2 }}>
            {buyer.name || 'Buyer'}
          </div>
          {buyer.address && (
            <div style={{ fontSize: 10, color: '#475569', whiteSpace: 'pre-line', marginTop: 2 }}>
              {buyer.address}
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, marginTop: 2, fontSize: 10, color: '#475569' }}>
            {buyer.phone && <span>Tel: {buyer.phone}</span>}
            {buyer.email && <span>{buyer.email}</span>}
          </div>
          {buyer.taxId && (
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>
              Tax ID: {buyer.taxId}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SupplierBlock({
  supplier,
  accent,
  showContact,
}: {
  supplier: PurchaseOrderPrintSupplier;
  accent: string;
  showContact: boolean;
}) {
  return (
    <div
      style={{
        padding: '10px 12px',
        border: `1px solid ${accent}33`,
        borderRadius: 4,
        background: '#fafafa',
      }}
    >
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          color: accent,
          marginBottom: 4,
        }}
      >
        Supplier
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{supplier.name || '—'}</div>
      {showContact && (
        <>
          {supplier.contactPerson && (
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>
              Attn: {supplier.contactPerson}
            </div>
          )}
          {supplier.address && (
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2, whiteSpace: 'pre-line' }}>
              {supplier.address}
            </div>
          )}
          {(supplier.phone || supplier.email) && (
            <div style={{ display: 'flex', gap: 10, marginTop: 2, fontSize: 10, color: '#475569' }}>
              {supplier.phone && <span>Tel: {supplier.phone}</span>}
              {supplier.email && <span>{supplier.email}</span>}
            </div>
          )}
          {supplier.taxId && (
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>
              Tax ID: {supplier.taxId}
            </div>
          )}
          {supplier.paymentTerms && (
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>
              Payment terms: {supplier.paymentTerms}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MetaBlock({ meta, accent }: { meta: PurchaseOrderPrintMeta; accent: string }) {
  return (
    <div
      style={{
        padding: '10px 12px',
        border: `1px solid ${accent}33`,
        borderRadius: 4,
        background: '#fafafa',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        rowGap: 4,
        columnGap: 8,
        alignContent: 'start',
      }}
    >
      <MetaLabel>PO Number</MetaLabel>
      <MetaValue>{meta.poNumber || '—'}</MetaValue>
      <MetaLabel>PO Date</MetaLabel>
      <MetaValue>{formatPrintDate(meta.poDateIso)}</MetaValue>
      {meta.status && (
        <>
          <MetaLabel>Status</MetaLabel>
          <MetaValue>{statusLabel(meta.status)}</MetaValue>
        </>
      )}
      <MetaLabel>Amounts</MetaLabel>
      <MetaValue>{formatAmountsAre(meta.amountsAre)}</MetaValue>
      {meta.reference && (
        <>
          <MetaLabel>Reference</MetaLabel>
          <MetaValue>{meta.reference}</MetaValue>
        </>
      )}
      {meta.preparedBy && (
        <>
          <MetaLabel>Prepared by</MetaLabel>
          <MetaValue>{meta.preparedBy}</MetaValue>
        </>
      )}
    </div>
  );
}

function MetaLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: '#475569',
      }}
    >
      {children}
    </span>
  );
}

function MetaValue({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: 11, color: '#0f172a' }}>{children}</span>;
}

function LineItemsTable({
  items,
  accent,
  showSku,
  showBonus,
  showRemarks,
  formatMoney,
}: {
  items: PurchaseOrderPrintLineItem[];
  accent: string;
  showSku: boolean;
  showBonus: boolean;
  showRemarks: boolean;
  formatMoney: (amount: number) => string;
}) {
  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: 16,
        fontSize: 10.5,
      }}
    >
      <thead>
        <tr style={{ background: accent, color: '#ffffff' }}>
          <Th width="24px">#</Th>
          <Th>Item / Description</Th>
          {showSku && <Th width="100px">SKU</Th>}
          <Th width="60px">Unit</Th>
          <Th width="70px" align="right">
            Qty
          </Th>
          {showBonus && (
            <Th width="60px" align="right">
              Bonus
            </Th>
          )}
          <Th width="85px" align="right">
            Rate
          </Th>
          <Th width="95px" align="right">
            Amount
          </Th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 ? (
          <tr>
            <Td colSpan={showSku ? (showBonus ? 8 : 7) : showBonus ? 7 : 6}>
              <span style={{ color: '#64748b', fontStyle: 'italic' }}>
                No line items on this purchase order.
              </span>
            </Td>
          </tr>
        ) : (
          items.map((item, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
              <Td align="center">{idx + 1}</Td>
              <Td>
                <div style={{ fontWeight: 600 }}>{item.itemName || '—'}</div>
                {showRemarks && item.remarks && (
                  <div style={{ fontSize: 9.5, color: '#64748b', marginTop: 2 }}>
                    {item.remarks}
                  </div>
                )}
              </Td>
              {showSku && <Td>{item.sku || '—'}</Td>}
              <Td>{item.unit || '—'}</Td>
              <Td align="right">{item.quantityOrdered.toLocaleString()}</Td>
              {showBonus && (
                <Td align="right">
                  {item.bonusQuantity > 0 ? item.bonusQuantity.toLocaleString() : '—'}
                </Td>
              )}
              <Td align="right">{formatMoney(item.rate)}</Td>
              <Td align="right" bold>
                {formatMoney(item.amount)}
              </Td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function Th({
  children,
  width,
  align = 'left',
}: {
  children: React.ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '6px 8px',
        textAlign: align,
        fontWeight: 700,
        fontSize: 9.5,
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        width,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
  bold = false,
  colSpan,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  bold?: boolean;
  colSpan?: number;
}) {
  return (
    <td
      colSpan={colSpan}
      style={{
        padding: '6px 8px',
        textAlign: align,
        fontWeight: bold ? 600 : 400,
        verticalAlign: 'top',
      }}
    >
      {children}
    </td>
  );
}

function TotalsBlock({
  totals,
  accent,
  formatMoney,
  meta,
}: {
  totals: PurchaseOrderPrintTotals;
  accent: string;
  formatMoney: (amount: number) => string;
  meta: PurchaseOrderPrintMeta;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 12,
      }}
    >
      <div
        style={{
          minWidth: 240,
          border: `1px solid ${accent}33`,
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <TotalsRow label="Subtotal" value={formatMoney(totals.subtotal)} />
        <TotalsRow label={formatAmountsAre(meta.amountsAre)} value="" small />
        <TotalsRow
          label="Total Amount"
          value={formatMoney(totals.totalAmount)}
          accentBg={accent}
          inverse
        />
      </div>
    </div>
  );
}

function TotalsRow({
  label,
  value,
  small = false,
  accentBg,
  inverse = false,
}: {
  label: string;
  value: string;
  small?: boolean;
  accentBg?: string;
  inverse?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '6px 10px',
        background: accentBg || 'transparent',
        color: inverse ? '#ffffff' : '#0f172a',
        fontWeight: inverse ? 700 : 500,
        fontSize: small ? 9.5 : 11,
        borderTop: '1px solid #e2e8f0',
      }}
    >
      <span style={inverse ? { letterSpacing: 0.6, textTransform: 'uppercase' } : undefined}>
        {label}
      </span>
      <span>{value}</span>
    </div>
  );
}

function TermsBlock({
  accent,
  termsText,
  notes,
}: {
  accent: string;
  termsText: string;
  notes: string;
}) {
  return (
    <div style={{ marginTop: 16 }}>
      {notes.trim() && (
        <div style={{ marginBottom: 8 }}>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              color: accent,
              marginBottom: 3,
            }}
          >
            Notes
          </div>
          <div style={{ fontSize: 10.5, color: '#1f2937', whiteSpace: 'pre-line' }}>{notes}</div>
        </div>
      )}
      {termsText.trim() && (
        <div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              color: accent,
              marginBottom: 3,
            }}
          >
            Terms &amp; Conditions
          </div>
          <div style={{ fontSize: 10, color: '#475569', whiteSpace: 'pre-line' }}>{termsText}</div>
        </div>
      )}
    </div>
  );
}

function SignatureBlock({
  accent,
  preparedByLabel,
  approvedByLabel,
  supplierAckLabel,
  preparedByName,
}: {
  accent: string;
  preparedByLabel: string;
  approvedByLabel: string;
  supplierAckLabel: string;
  preparedByName: string;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 18,
        marginTop: 26,
      }}
    >
      <SignatureSlot label={preparedByLabel} accent={accent} prefill={preparedByName} />
      <SignatureSlot label={approvedByLabel} accent={accent} />
      <SignatureSlot label={supplierAckLabel} accent={accent} />
    </div>
  );
}

function SignatureSlot({
  label,
  accent,
  prefill,
}: {
  label: string;
  accent: string;
  prefill?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ height: 28, borderBottom: `1px solid ${accent}88`, paddingLeft: 4 }}>
        {prefill && (
          <span style={{ fontSize: 10, color: '#475569', lineHeight: '28px' }}>{prefill}</span>
        )}
      </div>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: '#475569',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 9, color: '#94a3b8' }}>Date: ____________________</div>
    </div>
  );
}
